import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, ExternalLink, User } from 'lucide-react';
import { VendorPublicProfile } from '../types';
import VendorBadgeDisplay from './VendorBadgeDisplay';
import VendorRatingBadge from './VendorRatingBadge';

interface VendorCardProps {
  vendor: VendorPublicProfile;
  onBookmark?: (vendorId: number) => void;
  isBookmarked?: boolean;
}

export default function VendorCard({ vendor, onBookmark, isBookmarked }: VendorCardProps) {
  const firstImage = vendor.portfolio_images?.[0];
  const productTypes = vendor.product_types?.slice(0, 3) || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image/Placeholder */}
      <Link to={`/vendors/${vendor.id}`} className="block aspect-video relative bg-gray-100">
        {firstImage ? (
          <img
            src={firstImage}
            alt={`${vendor.business_name} portfolio preview`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200">
            <User className="w-16 h-16 text-teal-400" />
          </div>
        )}
        {/* Image count badge */}
        {vendor.portfolio_images && vendor.portfolio_images.length > 1 && (
          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
            +{vendor.portfolio_images.length - 1} more
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-4">
        {/* Business Name */}
        <Link to={`/vendors/${vendor.id}`}>
          <h3
            className="text-lg font-semibold text-gray-900 hover:text-secondary transition-colors line-clamp-1"
            title={vendor.business_name}
          >
            {vendor.business_name}
          </h3>
        </Link>

        {/* Description */}
        {vendor.description && (
          <p className="text-gray-600 text-sm mt-1 line-clamp-2">
            {vendor.description}
          </p>
        )}

        {/* Product Types */}
        {productTypes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {productTypes.map(type => (
              <span
                key={type}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-primary-dark rounded text-xs"
              >
                <Tag className="w-3 h-3" />
                {type}
              </span>
            ))}
            {vendor.product_types && vendor.product_types.length > 3 && (
              <span className="px-2 py-0.5 text-gray-500 text-xs">
                +{vendor.product_types.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Rating Badge (Issue #956, #1132, #1198) */}
        {vendor.rating_summary && vendor.rating_summary.total_reviews > 0 && (
          <div className="mt-3">
            <VendorRatingBadge
              vendorId={vendor.id}
              averageRating={vendor.rating_summary.average_rating ? parseFloat(vendor.rating_summary.average_rating.toString()) : undefined}
              totalReviews={vendor.rating_summary.total_reviews}
              size="sm"
            />
          </div>
        )}

        {/* Achievement Badges (Issue #684, #955) */}
        <div className="mt-3">
          <VendorBadgeDisplay vendorId={vendor.id} compact />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <Link
            to={`/vendors/${vendor.id}`}
            className="inline-flex items-center gap-1 text-sm text-secondary hover:text-primary-dark font-medium"
          >
            View Profile
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>

          {onBookmark && (
            <button
              onClick={() => onBookmark(vendor.id)}
              className={`p-1.5 rounded-full transition-colors ${
                isBookmarked
                  ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={isBookmarked ? 'Remove bookmark' : 'Bookmark vendor'}
            >
              <svg
                className="w-5 h-5"
                fill={isBookmarked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
